import {
  Activity,
  BarChart3,
  Bell,
  Bot,
  Building2,
  CalendarDays,
  CheckCircle2,
  ClipboardSignature,
  CreditCard,
  Database,
  FileText,
  Gauge,
  Globe2,
  Layers3,
  LockKeyhole,
  MessageSquareText,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  Users,
  Workflow,
} from 'lucide-react';

export const navItems = [
  { label: 'Home', to: '/' },
  { label: 'Features', to: '/features' },
  { label: 'Solutions', to: '/solutions' },
  { label: 'Industries', to: '/industries' },
  { label: 'Demo', to: '/demo' },
  { label: 'Pricing', to: '/pricing' },
];

export const resourceItems = [
  { label: 'Roadmap', to: '/roadmap' },
  { label: 'Blog', to: '/blog' },
  { label: 'FAQ', to: '/faq' },
];

export const problems = [
  'Manual Work',
  'Spreadsheets',
  'Multiple Apps',
  'Invoices',
  'Contracts',
  'Bookings',
  'Payments',
  'No Automation',
  'No Analytics',
];

export const features = [
  { title: 'AI Assistant', icon: Bot, text: 'Ask Tavio to create invoices, summarize revenue, draft contracts, and spot operational risks.' },
  { title: 'Booking Management', icon: CalendarDays, text: 'Manage rooms, desks, resources, credits, capacity, and recurring reservations in one calendar.' },
  { title: 'Client CRM', icon: Users, text: 'Track leads, members, contracts, renewals, messages, and every customer relationship milestone.' },
  { title: 'Invoice Automation', icon: ReceiptText, text: 'Generate invoices, payment reminders, receipts, and tax-ready summaries with less admin work.' },
  { title: 'Credit Tracking', icon: Gauge, text: 'Monitor included credits, overages, usage patterns, and plan-level entitlements automatically.' },
  { title: 'Analytics', icon: BarChart3, text: 'See revenue, occupancy, churn, bookings, invoices, and client growth from live dashboards.' },
  { title: 'Contract Generator', icon: ClipboardSignature, text: 'Create, store, and send workspace agreements with approval and signature workflows.' },
  { title: 'Role Management', icon: ShieldCheck, text: 'Give managers, finance teams, community teams, and executives the right level of access.' },
  { title: 'Document Storage', icon: Database, text: 'Centralize contracts, invoices, IDs, reports, and workspace documents for every client.' },
];

export const solutions = [
  { title: 'AI Assistant', icon: Sparkles },
  { title: 'Automation', icon: Workflow },
  { title: 'Analytics', icon: Activity },
  { title: 'CRM', icon: Users },
  { title: 'Bookings', icon: CalendarDays },
  { title: 'Payments', icon: CreditCard },
  { title: 'Reports', icon: FileText },
  { title: 'Contracts', icon: ClipboardSignature },
];

export const integrations = ['Stripe', 'Google Calendar', 'Microsoft Outlook', 'Zoom', 'Slack', 'Microsoft Teams', 'QuickBooks', 'Zapier', 'OpenAI'];

export const industries = [
  'Coworking Spaces',
  'Business Centers',
  'Commercial Offices',
  'Universities',
  'Innovation Hubs',
  'Shared Offices',
  'Enterprise Operators',
  'Consulting Firms',
];

export const dashboardStats = [
  { label: 'Revenue', value: '$84.2K', trend: '+18%' },
  { label: 'Bookings', value: '1,248', trend: '+31%' },
  { label: 'Clients', value: '436', trend: '+12%' },
  { label: 'Occupancy', value: '87%', trend: '+9%' },
];

export const workflow = ['Lead', 'Onboarding', 'Contract', 'Payment', 'Credits', 'Booking', 'Invoice', 'Reports', 'Analytics'];

export const pricingPlans = [
  {
    name: 'Starter',
    monthly: 79,
    annual: 63,
    text: 'For new shared workspaces moving away from spreadsheets.',
    features: ['Bookings', 'Client CRM', 'Invoices', 'Email support'],
  },
  {
    name: 'Growth',
    monthly: 199,
    annual: 159,
    text: 'For teams that need automation, analytics, and AI support.',
    featured: true,
    features: ['AI assistant', 'Automations', 'Contracts', 'Analytics', 'Priority support'],
  },
  {
    name: 'Enterprise',
    monthly: 499,
    annual: 399,
    text: 'For multi-location operators and enterprise workspace teams.',
    features: ['SSO-ready', 'Advanced roles', 'Custom workflows', 'Dedicated success'],
  },
];

export const testimonials = [
  { quote: 'Tavio makes our front desk, finance, and management workflows feel connected for the first time.', name: 'Mira Patel', role: 'Workspace Director' },
  { quote: 'The AI assistant turns operational questions into answers before our weekly meeting even starts.', name: 'Jonas Reed', role: 'Business Center COO' },
  { quote: 'We finally have a clean source of truth for members, bookings, invoices, and contracts.', name: 'Amara Lee', role: 'Innovation Hub Lead' },
];

export const faqs = [
  ['Is Tavio a real SaaS app today?', 'This website is a marketing prototype. Product workflows use mock data and placeholder forms.'],
  ['Who is Tavio for?', 'Coworking spaces, shared offices, business centers, universities, incubators, and enterprise workspace operators.'],
  ['Can it connect with payments?', 'The planned product can support payment integrations such as Stripe and accounting tools such as QuickBooks.'],
  ['Does the AI assistant use real data?', 'In this prototype it uses sample scenarios only. A production version would connect to secure workspace data.'],
  ['Can we deploy this to GitHub Pages?', 'Yes. The Vite base configuration and deployment script are ready for GitHub Pages workflows.'],
  ['Does it support dark mode?', 'Yes. The site includes a persistent light and dark mode toggle.'],
  ['Is the contact form live?', 'The form is wired as a Formspree placeholder and can be connected by changing the endpoint.'],
  ['Can Tavio manage contracts?', 'The product concept includes contract generation, storage, renewals, and digital signature workflows.'],
  ['Can teams use roles?', 'Yes. The concept includes permissions for managers, community teams, finance, and executives.'],
  ['Does it include analytics?', 'Yes. The website showcases revenue, occupancy, booking, invoice, and client analytics.'],
  ['Can we add a real app later?', 'The codebase is split into pages, data, layouts, and components so it can evolve without a rewrite.'],
  ['Are images required?', 'The prototype uses CSS-built dashboard visuals to stay lightweight and easy to customize.'],
  ['Does it include SEO metadata?', 'Every route can define titles, descriptions, canonical links, and structured data.'],
  ['Is it accessible?', 'Navigation, forms, buttons, focus states, semantic sections, and accordions are built with accessibility in mind.'],
  ['Can pricing be changed?', 'Pricing data lives in a shared data file and can be replaced by CMS or billing data later.'],
  ['Can there be a mobile app?', 'The roadmap includes a mobile app as a future milestone.'],
  ['Can it support multiple locations?', 'Enterprise positioning includes multi-location operations and advanced roles.'],
  ['What analytics are included?', 'Google Analytics and Microsoft Clarity placeholders are documented in the setup.'],
  ['Can integrations be expanded?', 'Yes. Integrations are data-driven and easy to extend.'],
  ['Is this production-ready?', 'It is production-quality as a static marketing site, with placeholders where a backend would normally be required.'],
];

export const roadmap = [
  'Marketing Website',
  'Private Beta',
  'Public Beta',
  'AI Assistant',
  'Mobile App',
  'Marketplace',
  'API',
  'Enterprise',
];

export const blogPosts = [
  { title: 'How AI changes workspace operations', category: 'AI', tag: 'Automation', minutes: 5 },
  { title: 'The metrics every coworking operator should track', category: 'Analytics', tag: 'Revenue', minutes: 7 },
  { title: 'From lead to invoice in one connected workflow', category: 'Operations', tag: 'CRM', minutes: 4 },
  { title: 'Designing premium member experiences at scale', category: 'Experience', tag: 'Growth', minutes: 6 },
];

export const trustSignals = [
  { icon: LockKeyhole, label: 'Enterprise-ready access controls' },
  { icon: Globe2, label: 'Multi-location operating model' },
  { icon: Bell, label: 'Automated reminders and alerts' },
  { icon: MessageSquareText, label: 'AI-assisted support workflows' },
  { icon: Layers3, label: 'Modular SaaS architecture' },
  { icon: CheckCircle2, label: 'Built for operational reliability' },
  { icon: Building2, label: 'Designed for modern workspaces' },
];
